import React, { useEffect, useMemo } from 'react';
import { SkuRedistributionResponse } from '@ydistri/api-sdk';
import { useSkuRedistributionTableColumns } from '../../Redistribution/parts/listing/components/SkuRedistributionTable/useSkuRedistributionTableColumns';
import { useCurrency } from '../../../../hooks/useCurrency';
import { computeRemSize } from '@ydistri/theme';
import { buildPickingPositionsStats } from '../EditRedistributionPickingPositions/pickingPositionsStats';
import { ChangelogSectionProps, parseChangelogData } from '../editRedistributionLib';
import { DataTable } from '@ydistri/ds';
import { formatNumber } from '@ydistri/generic-utils';

/** A changed picking position carries the quantity it had before the change. */
const quantityBefore = (row: SkuRedistributionResponse): number | undefined =>
  'quantityBefore' in row && typeof row.quantityBefore === 'number'
    ? row.quantityBefore
    : undefined;

const ChangelogPickingPosition: React.FC<ChangelogSectionProps> = ({
  stringifiedData,
  onStatsComputed,
}) => {
  const tableColumns = useSkuRedistributionTableColumns();
  const currency = useCurrency();

  const columns = useMemo(
    () =>
      tableColumns.map(column =>
        column.key === 'quantity'
          ? {
              ...column,
              width: '9rem',
              render: (value: number, row: SkuRedistributionResponse, index: number) => {
                const before = quantityBefore(row);
                return before === undefined
                  ? column.render?.(value, row, index)
                  : `${formatNumber(before, 0)} → ${formatNumber(value, 0)}`;
              },
            }
          : column,
      ),
    [tableColumns],
  );

  const data = useMemo(
    () => parseChangelogData<SkuRedistributionResponse>(stringifiedData),
    [stringifiedData],
  );

  useEffect(() => {
    onStatsComputed(buildPickingPositionsStats(data, currency));
  }, [data, currency, onStatsComputed]);

  if (data.length === 0) return null;

  return (
    <DataTable<SkuRedistributionResponse>
      id="changelog-redistribution-single-picking-position-table"
      showHeader={true}
      columns={columns}
      height={computeRemSize(400)}
      dataSource={data}
    />
  );
};

export default ChangelogPickingPosition;
